import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  Plus, PencilSimple, UserCircle, ShieldCheck, Power, Eye,
  Trash, EnvelopeSimple, Key, X, CheckSquare, Square,
  Warning, MagnifyingGlass,
} from "@phosphor-icons/react";

const ROLE_STYLE = {
  office: "bg-[#D4E0D1] text-[#1A3317]",
  office_staff: "bg-[#D4E0D1] text-[#1A3317]",
  manager: "bg-[#F9E4B7] text-[#7A5A11]",
  hr: "bg-[#F4C7B9] text-[#7A2E18]",
};

export default function OfficeStaffList() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(() => new Set());
  const [confirmDel, setConfirmDel] = useState(false);
  const [emailEdit, setEmailEdit] = useState(null);   // {staff}
  const [pwResend, setPwResend] = useState(null);     // staff
  const [availableRoles, setAvailableRoles] = useState([]);  // [{key,label,builtin}]
  // Iter 138 — surface hidden accounts (soft-deleted + orphaned role)
  const [showDeleted, setShowDeleted] = useState(false);
  const [hardDel, setHardDel] = useState(null);       // staff to permanently purge
  const [showDiag, setShowDiag] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    try {
      const r = await api.get(`/office-staff${showDeleted ? "?include_deleted=true" : ""}`);
      setItems(r.data);
      setSelected(new Set());
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => {
    load();
    // Load the full assignable-role list (built-in + tenant custom roles)
    // so the filter buttons cover every role the tenant actually uses.
    api.get("/office-staff/available-roles")
      .then(r => setAvailableRoles(r.data.roles || []))
      .catch(() => setAvailableRoles([]));
  }, [showDeleted]);

  const toggle = async (s) => {
    const path = s.status === "inactive" ? "activate" : "deactivate";
    if (!window.confirm(`${path === "activate" ? "Activate" : "Deactivate"} ${s.name}?`)) return;
    try {
      await api.post(`/office-staff/${s.id}/${path}`);
      toast.success(`Staff ${path}d`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const rows = useMemo(
    () => (filter === "all" ? items : items.filter(i => i.role === filter)),
    [items, filter],
  );

  const allSelected = rows.length > 0 && rows.every(r => selected.has(r.id));
  const someSelected = selected.size > 0;
  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(rows.map(r => r.id)));
  };
  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  return (
    <AppShell title="Staff Management" subtitle="Office / Manager / HR"
      actions={
        <div className="flex items-center gap-2">
          <button onClick={() => setShowDiag(true)}
                  data-testid="btn-office-diag"
                  title="Look up any user by email/phone/id (superadmin diagnostic)"
                  className="inline-flex items-center gap-2 h-10 px-3 border border-[#E5E2D9] rounded-md text-sm hover:bg-[#F7F6F2]">
            <MagnifyingGlass size={14}/>Lookup
          </button>
          <label className="inline-flex items-center gap-2 h-10 px-3 border border-[#E5E2D9] rounded-md text-sm cursor-pointer hover:bg-[#F7F6F2]"
                 data-testid="toggle-show-deleted">
            <input type="checkbox" checked={showDeleted}
                   onChange={e => setShowDeleted(e.target.checked)}
                   className="accent-[#C25D38]"/>
            Show deleted
          </label>
          {someSelected && (
            <button data-testid="btn-bulk-delete-office"
                    onClick={() => setConfirmDel(true)}
                    className="inline-flex items-center gap-2 h-10 px-3 bg-[#C25D38] hover:bg-[#A94C2C] text-white rounded-md text-sm font-medium">
              <Trash size={14} weight="bold"/>Delete selected ({selected.size})
            </button>
          )}
          <button data-testid="btn-new-office-staff" onClick={() => nav("/admin/staff-mgmt/new")}
                  className="inline-flex items-center gap-2 h-10 px-4 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded-md text-sm font-medium">
            <Plus size={14} weight="bold"/>Add staff
          </button>
        </div>
      }
    >
      <div className="flex gap-2 flex-wrap mb-4" data-testid="role-filter">
        {(() => {
          const dyn = availableRoles.map(r => ({ key: r.key, label: r.label }));
          const chips = [{ key: "all", label: "All" }, ...dyn];
          return chips.map(r => (
            <button key={r.key} onClick={() => setFilter(r.key)}
                    data-testid={`filter-${r.key}`}
                    className={`px-3 h-9 rounded-md text-xs uppercase tracking-[0.18em] border ${filter === r.key ? "bg-[#2D5A27] text-white border-[#2D5A27]" : "bg-white border-[#E5E2D9] text-[#5C5A55]"}`}>
              {r.label}
            </button>
          ));
        })()}
      </div>

      <div className="bg-white border border-[#E5E2D9] rounded-md overflow-x-auto">
        <table className="w-full text-sm" data-testid="office-staff-table">
          <thead>
            <tr className="bg-[#F7F6F2] text-left text-[11px] uppercase tracking-[0.2em] text-[#5C5A55]">
              <th className="px-4 py-3 w-8">
                <button onClick={toggleAll} data-testid="office-select-all"
                        className="text-[#2D5A27] hover:opacity-80">
                  {allSelected ? <CheckSquare size={16} weight="fill"/> : <Square size={16}/>}
                </button>
              </th>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Code</th>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Role</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Duty</th>
              <th className="px-4 py-3 text-right">Rate</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={9} className="px-6 py-12 text-center text-[#8A8780]">No staff.</td></tr>}
            {rows.map((s, i) => (
              <tr key={s.id}
                  className={`${i % 2 === 0 ? "bg-white" : "bg-[#FAFAF7]"} ${s.is_deleted ? "opacity-70" : ""}`}
                  data-testid={`office-row-${s.id}`}>
                <td className="px-4 py-3">
                  <button onClick={() => toggleOne(s.id)}
                          data-testid={`office-select-${s.id}`}
                          className="text-[#2D5A27] hover:opacity-80">
                    {selected.has(s.id) ? <CheckSquare size={16} weight="fill"/> : <Square size={16}/>}
                  </button>
                </td>
                <td className="px-4 py-3 font-medium">
                  <div className="flex items-center gap-2 flex-wrap">
                    <UserCircle size={18} color="#2D5A27" weight="duotone"/> {s.name}
                    {s.is_deleted && (
                      <span data-testid={`badge-deleted-${s.id}`}
                            className="px-1.5 py-0.5 text-[9px] uppercase tracking-[0.16em] bg-[#7A2E18] text-white rounded">Deleted</span>
                    )}
                    {s.role_orphan && (
                      <span data-testid={`badge-orphan-${s.id}`}
                            title="This role key is not recognised by the current permissions matrix"
                            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] uppercase tracking-[0.16em] bg-[#C25D38] text-white rounded">
                        <Warning size={9} weight="bold"/>Role orphan
                      </span>
                    )}
                    {s.custom_role_deactivated && (
                      <span data-testid={`badge-role-off-${s.id}`}
                            title="This user's custom role has been deactivated but they can still log in"
                            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] uppercase tracking-[0.16em] bg-[#F9E4B7] text-[#7A5A11] rounded">
                        <Warning size={9} weight="bold"/>Role off
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 font-mono text-xs">{s.staff_code || "—"}</td>
                <td className="px-4 py-3 text-xs text-[#5C5A55]">
                  <div className="flex items-center gap-1">
                    <span>{s.email}</span>
                    <button onClick={() => setEmailEdit(s)}
                            data-testid={`edit-email-${s.id}`}
                            title="Change email"
                            className="text-[#0B2B66] hover:bg-[#EEF2FA] p-1 rounded">
                      <PencilSimple size={11}/>
                    </button>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] rounded ${ROLE_STYLE[s.role] || "bg-[#E5E2D9]"}`}>{s.role}</span>
                </td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] rounded ${s.status === "inactive" ? "bg-[#F4C7B9] text-[#7A2E18]" : "bg-[#D4E0D1] text-[#1A3317]"}`}>
                    {s.status || "active"}
                  </span>
                </td>
                <td className="px-4 py-3 text-xs">
                  {s.current_duty ? (
                    <span className="font-mono text-[#2D5A27]">● {s.current_duty.status}</span>
                  ) : <span className="text-[#8A8780]">—</span>}
                </td>
                <td className="px-4 py-3 text-right font-mono text-xs">₹{s.hourly_rate || 0}/hr</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <Link to={`/admin/staff-mgmt/${s.id}`} data-testid={`view-${s.id}`}
                        className="text-[#2D5A27] text-xs mr-2 inline-flex items-center gap-1"><Eye size={12}/>View</Link>
                  <Link to={`/admin/staff-mgmt/${s.id}/edit`} data-testid={`edit-${s.id}`}
                        className="text-[#2D5A27] text-xs mr-2 inline-flex items-center gap-1"><PencilSimple size={12}/>Edit</Link>
                  <button onClick={() => setPwResend(s)}
                          data-testid={`resend-pw-${s.id}`}
                          className="text-[#0B2B66] text-xs mr-2 inline-flex items-center gap-1"><Key size={12}/>Resend password</button>
                  <button onClick={() => toggle(s)} data-testid={`toggle-${s.id}`}
                          className={`text-xs inline-flex items-center gap-1 mr-2 ${s.status === "inactive" ? "text-[#2D5A27]" : "text-[#C25D38]"}`}>
                    <Power size={12}/>{s.status === "inactive" ? "Activate" : "Deactivate"}
                  </button>
                  <button onClick={() => setHardDel(s)} data-testid={`hard-del-${s.id}`}
                          title="Permanently remove from database (requires superadmin password)"
                          className="text-xs inline-flex items-center gap-1 text-[#7A2E18] hover:underline">
                    <Trash size={12} weight="bold"/>Hard delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex gap-3 flex-wrap">
        <Link to="/admin/staff-mgmt/attendance" className="h-10 px-4 inline-flex items-center gap-2 border border-[#E5E2D9] rounded-md text-sm hover:bg-[#F7F6F2]">
          <ShieldCheck size={14}/>Attendance & Time
        </Link>
        <Link to="/admin/staff-mgmt/payroll" className="h-10 px-4 inline-flex items-center gap-2 border border-[#E5E2D9] rounded-md text-sm hover:bg-[#F7F6F2]">
          Payroll Summary
        </Link>
        <Link to="/admin/staff-mgmt/roles" className="h-10 px-4 inline-flex items-center gap-2 border border-[#E5E2D9] rounded-md text-sm hover:bg-[#F7F6F2]">
          Roles & Permissions
        </Link>
      </div>

      {confirmDel && (
        <PasswordConfirmModal
          count={selected.size}
          label="office staff"
          onClose={() => setConfirmDel(false)}
          onConfirm={async (password) => {
            try {
              const r = await api.post("/office-staff/bulk-delete", {
                ids: Array.from(selected), password,
              });
              toast.success(`Deleted ${r.data.soft_deleted ?? r.data.hard_deleted ?? 0} staff`);
              setConfirmDel(false);
              load();
            } catch (e) { toast.error(formatApiError(e)); }
          }}
        />
      )}

      {emailEdit && (
        <EmailEditModal
          staff={emailEdit}
          onClose={() => setEmailEdit(null)}
          onSaved={() => { setEmailEdit(null); load(); }}
        />
      )}

      {pwResend && (
        <PasswordResendModal
          staff={pwResend}
          onClose={() => setPwResend(null)}
        />
      )}

      {hardDel && (
        <HardDeleteModal
          staff={hardDel}
          onClose={() => setHardDel(null)}
          onDone={() => { setHardDel(null); load(); }}
        />
      )}

      {showDiag && (
        <DiagnosticLookupModal
          onClose={() => setShowDiag(false)}
          onHardDelete={(u) => { setShowDiag(false); setHardDel(u); }}
        />
      )}
    </AppShell>
  );
}


// ─────────────────────────────────────────────────────────────
// Password confirm modal — reused for bulk-delete gate
// ─────────────────────────────────────────────────────────────
export function PasswordConfirmModal({ count, label = "records", onClose, onConfirm }) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setBusy(true);
    await onConfirm(password);
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
         onClick={onClose}>
      <form onSubmit={submit} onClick={e => e.stopPropagation()}
            data-testid="pw-confirm-modal"
            className="bg-white rounded-md w-full max-w-md">
        <div className="p-6 border-b border-[#E5E2D9] flex justify-between items-center">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#C25D38]">Destructive action</div>
            <div className="font-heading text-lg mt-1">Delete {count} {label}?</div>
          </div>
          <button type="button" onClick={onClose} className="p-1 hover:bg-[#F7F6F2] rounded">
            <X size={16}/>
          </button>
        </div>
        <div className="p-6 space-y-3">
          <p className="text-sm text-[#5C5A55]">
            Records will be <b>soft-deleted</b> (marked inactive, retrievable).
            Enter your admin password to confirm.
          </p>
          <input type="password" autoFocus autoComplete="current-password"
                 value={password} onChange={e => setPassword(e.target.value)}
                 data-testid="pw-confirm-input"
                 placeholder="Your admin password"
                 className="w-full h-11 px-3 border border-[#E5E2D9] rounded-md text-sm focus:border-[#C25D38] outline-none"/>
        </div>
        <div className="p-6 border-t border-[#E5E2D9] flex justify-end gap-2">
          <button type="button" onClick={onClose}
                  className="h-10 px-4 border border-[#E5E2D9] rounded-md text-sm">Cancel</button>
          <button type="submit" disabled={busy || !password}
                  data-testid="pw-confirm-submit"
                  className="h-10 px-5 bg-[#C25D38] hover:bg-[#A94C2C] disabled:opacity-50 text-white rounded-md text-sm font-medium">
            {busy ? "Deleting…" : "Confirm delete"}
          </button>
        </div>
      </form>
    </div>
  );
}


function EmailEditModal({ staff, onClose, onSaved }) {
  const [email, setEmail] = useState(staff.email || "");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await api.post(`/office-staff/${staff.id}/change-email`, { email: email.trim() });
      toast.success("Email updated");
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={e => e.stopPropagation()}
            data-testid="office-email-edit-modal"
            className="bg-white rounded-md w-full max-w-md">
        <div className="p-6 border-b border-[#E5E2D9] flex justify-between items-center">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55]">Change email</div>
            <div className="font-heading text-lg mt-1">{staff.name}</div>
          </div>
          <button type="button" onClick={onClose} className="p-1 hover:bg-[#F7F6F2] rounded"><X size={16}/></button>
        </div>
        <div className="p-6 space-y-3">
          <div className="text-xs text-[#5C5A55]">Current: <span className="font-mono">{staff.email}</span></div>
          <input type="email" required autoFocus value={email}
                 onChange={e => setEmail(e.target.value)}
                 data-testid="office-email-input"
                 placeholder="new.email@example.com"
                 className="w-full h-11 px-3 border border-[#E5E2D9] rounded-md text-sm focus:border-[#2D5A27] outline-none"/>
          <p className="text-[11px] text-[#8A8780]">
            The staff member will need to use this new email to log in.
          </p>
        </div>
        <div className="p-6 border-t border-[#E5E2D9] flex justify-end gap-2">
          <button type="button" onClick={onClose}
                  className="h-10 px-4 border border-[#E5E2D9] rounded-md text-sm">Cancel</button>
          <button type="submit" disabled={busy || !email.trim() || email === staff.email}
                  data-testid="office-email-save"
                  className="h-10 px-5 bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-50 text-white rounded-md text-sm font-medium">
            {busy ? "Saving…" : "Update email"}
          </button>
        </div>
      </form>
    </div>
  );
}


function PasswordResendModal({ staff, onClose }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const send = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/office-staff/${staff.id}/resend-password`);
      setResult(r.data);
      if (r.data.email_status === "failed") {
        toast.error("Password reset — email delivery failed");
      } else {
        toast.success(`New password emailed to ${r.data.sent_to}`);
      }
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
           data-testid="pw-resend-modal"
           className="bg-white rounded-md w-full max-w-md">
        <div className="p-6 border-b border-[#E5E2D9] flex justify-between items-center">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55]">Resend password</div>
            <div className="font-heading text-lg mt-1">{staff.name}</div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-[#F7F6F2] rounded"><X size={16}/></button>
        </div>
        <div className="p-6 space-y-3">
          {!result && (
            <>
              <p className="text-sm text-[#5C5A55]">
                This will generate a new temporary password and email it to
                <b className="mx-1">{staff.email}</b>
                along with the production login URL. The staff member should
                change it after first login.
              </p>
              <button onClick={send} disabled={busy}
                      data-testid="pw-resend-send"
                      className="w-full h-11 inline-flex items-center justify-center gap-2 bg-[#0B2B66] hover:bg-[#0f3579] text-white rounded-md text-sm font-medium disabled:opacity-50">
                <EnvelopeSimple size={13}/>
                {busy ? "Sending…" : "Generate & send new password"}
              </button>
            </>
          )}
          {result && (
            <div className={`p-4 rounded-md text-sm ${result.email_status === "failed" ? "bg-[#FDE9E4] text-[#7A2E18]" : "bg-[#D4E0D1] text-[#1A3317]"}`}>
              {result.email_status === "failed"
                ? <>Password reset, but the email failed: <br/><span className="font-mono text-xs">{result.error}</span></>
                : <>New password emailed to <b>{result.sent_to}</b>. The user&apos;s old password no longer works.</>}
            </div>
          )}
        </div>
        <div className="p-6 border-t border-[#E5E2D9] flex justify-end gap-2">
          <button onClick={onClose}
                  className="h-10 px-4 border border-[#E5E2D9] rounded-md text-sm">Close</button>
        </div>
      </div>
    </div>
  );
}



// ─────────────────────────────────────────────────────────────
// Hard-delete confirmation — permanently purges from DB.
// Requires superadmin's password. Works on ANY user (bypasses
// the office-role allow-list) so orphaned accounts can be purged.
// ─────────────────────────────────────────────────────────────
function HardDeleteModal({ staff, onClose, onDone }) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setBusy(true);
    try {
      await api.post(`/office-staff/${staff.id}/hard-delete`, { password });
      toast.success(`${staff.name || staff.email} permanently deleted`);
      onDone();
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={e => e.stopPropagation()}
            data-testid="hard-delete-modal"
            className="bg-white rounded-md w-full max-w-md">
        <div className="p-6 border-b border-[#E5E2D9] flex justify-between items-center">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#7A2E18]">Permanent · Cannot be undone</div>
            <div className="font-heading text-lg mt-1">Hard delete {staff.name || staff.email}?</div>
          </div>
          <button type="button" onClick={onClose} className="p-1 hover:bg-[#F7F6F2] rounded"><X size={16}/></button>
        </div>
        <div className="p-6 space-y-3">
          <div className="bg-[#FDE9E4] border border-[#F4C7B9] rounded-md p-3 text-xs text-[#7A2E18] leading-relaxed">
            <div className="flex items-center gap-1 mb-1 font-medium uppercase tracking-[0.16em]">
              <Warning size={12} weight="bold"/>Danger zone
            </div>
            The user row will be <b>removed from the database entirely</b>.
            Audit / attendance / payroll history rows that reference this
            user by id will remain but the user won&apos;t be able to log
            in and their name will no longer resolve in future audit
            entries. If you only want to disable login, use{" "}
            <b>Deactivate</b> instead.
          </div>
          <div className="text-sm space-y-0.5">
            <div><b>Name:</b> {staff.name || "—"}</div>
            <div><b>Email:</b> <span className="font-mono">{staff.email || "—"}</span></div>
            <div><b>Role:</b> <span className="font-mono">{staff.role || "—"}</span></div>
            <div><b>Status:</b> <span className="font-mono">{staff.status || "—"}</span></div>
            {staff.deleted_at && <div className="text-[#7A2E18]"><b>Soft-deleted at:</b> {staff.deleted_at}</div>}
          </div>
          <input type="password" autoFocus autoComplete="current-password"
                 value={password} onChange={e => setPassword(e.target.value)}
                 data-testid="hard-del-pw"
                 placeholder="Enter your superadmin password"
                 className="w-full h-11 px-3 border border-[#E5E2D9] rounded-md text-sm focus:border-[#7A2E18] outline-none"/>
        </div>
        <div className="p-6 border-t border-[#E5E2D9] flex justify-end gap-2">
          <button type="button" onClick={onClose}
                  className="h-10 px-4 border border-[#E5E2D9] rounded-md text-sm">Cancel</button>
          <button type="submit" disabled={busy || !password}
                  data-testid="hard-del-confirm"
                  className="h-10 px-5 bg-[#7A2E18] hover:bg-[#5F2312] disabled:opacity-50 text-white rounded-md text-sm font-medium">
            {busy ? "Deleting…" : "Permanently delete"}
          </button>
        </div>
      </form>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// Diagnostic lookup — search ANY user by email/phone/id (superadmin).
// Surfaces accounts that are hidden from the main list due to
// deleted_at / deactivated custom role / off-list role key.
// ─────────────────────────────────────────────────────────────
function DiagnosticLookupModal({ onClose, onHardDelete }) {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);
  const search = async (e) => {
    e?.preventDefault();
    if (q.trim().length < 3) { toast.error("Enter at least 3 characters"); return; }
    setBusy(true);
    try {
      const r = await api.get(`/office-staff-search?q=${encodeURIComponent(q.trim())}`);
      setResults(r.data);
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
           data-testid="office-diag-modal"
           className="bg-white rounded-md w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-[#E5E2D9] flex justify-between items-center">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55]">Diagnostic · superadmin</div>
            <div className="font-heading text-lg mt-1">User lookup</div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-[#F7F6F2] rounded"><X size={16}/></button>
        </div>
        <form onSubmit={search} className="p-6 border-b border-[#E5E2D9] flex gap-2">
          <input autoFocus value={q} onChange={e => setQ(e.target.value)}
                 data-testid="diag-input"
                 placeholder="Email / phone / user id (min 3 chars)"
                 className="flex-1 h-10 px-3 border border-[#E5E2D9] rounded-md text-sm"/>
          <button type="submit" disabled={busy}
                  data-testid="diag-search-btn"
                  className="h-10 px-4 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded-md text-sm font-medium disabled:opacity-50">
            {busy ? "Searching…" : "Search"}
          </button>
        </form>
        <div className="p-6 text-sm">
          {results === null && (
            <div className="text-xs text-[#8A8780]">
              Searches across ALL users in this tenant (any role, any status,
              soft-deleted included). Use this to trace an account that can
              log in but doesn&apos;t appear in the main Staff list.
            </div>
          )}
          {results && results.count === 0 && (
            <div className="text-center py-6 text-[#8A8780]">No matches.</div>
          )}
          {results && results.count > 0 && (
            <div className="space-y-3">
              <div className="text-[11px] uppercase tracking-[0.18em] text-[#5C5A55]">
                {results.count} match{results.count === 1 ? "" : "es"}
              </div>
              {results.matches.map((u) => (
                <div key={u.id}
                     data-testid={`diag-result-${u.id}`}
                     className="border border-[#E5E2D9] rounded-md p-3 space-y-1 bg-[#FAFAF7]">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="font-medium">{u.name || <span className="text-[#8A8780]">(no name)</span>}</div>
                    <div className="flex items-center gap-1">
                      <span className={`px-1.5 py-0.5 text-[9px] uppercase tracking-[0.16em] rounded ${ROLE_STYLE[u.role] || "bg-[#E5E2D9]"}`}>
                        {u.role || "—"}
                      </span>
                      {u.is_deleted && (
                        <span className="px-1.5 py-0.5 text-[9px] uppercase tracking-[0.16em] bg-[#7A2E18] text-white rounded">Deleted</span>
                      )}
                      <span className={`px-1.5 py-0.5 text-[9px] uppercase tracking-[0.16em] rounded ${u.status === "inactive" ? "bg-[#F4C7B9] text-[#7A2E18]" : "bg-[#D4E0D1] text-[#1A3317]"}`}>
                        {u.status || "active"}
                      </span>
                    </div>
                  </div>
                  <div className="text-xs text-[#5C5A55] space-y-0.5">
                    <div>Email: <span className="font-mono">{u.email || "—"}</span></div>
                    <div>Phone: <span className="font-mono">{u.phone || "—"}</span></div>
                    <div>ID: <span className="font-mono text-[10px]">{u.id}</span></div>
                    {u.deleted_at && <div className="text-[#7A2E18]">Soft-deleted at: {u.deleted_at}</div>}
                    {u.created_at && <div>Created: {u.created_at}</div>}
                  </div>
                  {u.role !== "superadmin" && u.role !== "customer" && u.role !== "field_staff" && (
                    <div className="pt-2">
                      <button onClick={() => onHardDelete(u)}
                              data-testid={`diag-hard-del-${u.id}`}
                              className="text-xs inline-flex items-center gap-1 text-[#7A2E18] hover:underline">
                        <Trash size={11} weight="bold"/>Hard delete this account
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="p-6 border-t border-[#E5E2D9] flex justify-end">
          <button onClick={onClose}
                  className="h-10 px-4 border border-[#E5E2D9] rounded-md text-sm">Close</button>
        </div>
      </div>
    </div>
  );
}
